import { inject } from '@angular/core';
import { Routes, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateFn } from '@angular/router';

import { MetricsDashboardComponent } from './home/metrics-dashboard/metrics-dashboard.component';
import { AccountsListComponent } from './tools/accounts-list/accounts-list.component';
import { NotFoundComponent } from '../not-found/not-found.component';
import { AppsStore } from '../../stores/apps.store';
import { AuthenticationService } from 'projects/internal/r1-component-library/src/lib/services/appcloud';

export function isAuthorizedGuard(): CanActivateFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        const url: string = state.url;

        const isAuthorized = authService.isAuthorized();
        if (isAuthorized) {
            return true;
        }
        authService.redirectUrl = url;
        return router.parseUrl('/login');
    };
}

export function isLoggedInGuard(): CanActivateFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        const url: string = state.url;

        const isLoggedIn = authService.isAuthenticated();
        if (isLoggedIn) {
            return true;
        }
        authService.redirectUrl = url;
        return router.parseUrl('/login');
    };
}

export const ADMIN_ROUTES: Routes = [
    { path: 'metrics', component: MetricsDashboardComponent },
    {
        path: 'profile',
        children: [
            {
                path: 'manage',
                loadComponent: () => import('./profile/profile.component').then(c => c.ProfileComponent),
            },
            {
                path: 'switch-organization',
                loadComponent: () =>
                    import('./profile/switch-account/switch-account.component').then(c => c.SwitchAccountComponent),
            },
            {
                path: 'logout',
                loadComponent: () => import('./profile/logout/logout.component').then(c => c.LogoutComponent),
            },
            { path: '', pathMatch: 'full', redirectTo: 'manage' },
        ],
    },
    {
        path: 'dashboard',
        children: [
            {
                path: 'organizations-and-users',
                loadComponent: () =>
                    import('./dashboard/organizations-users/organizations-users-dashboard.component').then(
                        c => c.OrganizationsUsersDashboardComponent
                    ),
            },
            {
                path: 'licenses',
                loadComponent: () =>
                    import('./dashboard/licenses/licenses-dashboard.component').then(c => c.LicensesDashboardComponent),
            },
            {
                path: 'app-usage',
                loadComponent: () =>
                    import('./dashboard/app-usage/app-usage-dashboard.component').then(
                        c => c.AppUsageDashboardComponent
                    ),
            },
        ],
    },
    {
        path: 'tools',
        children: [
            { path: 'organizations', component: AccountsListComponent },
            {
                path: 'users',
                loadComponent: () => import('./tools/users-list/users-list.component').then(c => c.UsersListComponent),
            },
            {
                path: 'licenses',
                loadComponent: () => import('./tools/licenses/licenses.component').then(c => c.LicensesComponent),
            },
            {
                path: 'packages',
                loadComponent: () => import('./tools/packages/packages.component').then(c => c.PackagesComponent),
            },
            {
                path: 'apps',
                // One AppsStore instance shared by the list and the App Page
                providers: [AppsStore],
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        loadComponent: () => import('./tools/apps/apps.component').then(c => c.AppsComponent),
                    },
                    {
                        path: ':subscriptionId',
                        loadComponent: () =>
                            import('./tools/apps/app-detail/app-detail.component').then(c => c.AppDetailComponent),
                    },
                ],
            },
        ],
    },
    {
        path: '**',
        component: NotFoundComponent,
    },
];
